import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "@emotion/styled";
import { useSearchParams, useNavigate } from "react-router-dom";
// import { toast } from "react-toastify"; // REMOVER import direto do toast
import { BeatLoader } from "react-spinners";

import { showManagedToast, dismissManagedToast } from "@/util/toastManager"; // IMPORTAR o novo utilitário
import { fetchAuthToken } from "@/util/auth";
import { validateDNA } from "@/util/validateDNA";
import { fetchOperatorData } from "@/util/fetchOperatorData";
import { decodeJWT } from "@/util/decodeJWT";

/* ==========================================================================
 * ENV
 * ========================================================================== */
const ENV = (window as any).ENV || {};
const { VITE_VIDAS_URL, VITE_API_URL, VITE_NUVIDIO } = ENV;

if (!VITE_VIDAS_URL || !VITE_API_URL || !VITE_NUVIDIO) {
  const missingVars = [];
  if (!VITE_VIDAS_URL) missingVars.push("VITE_VIDAS_URL");
  if (!VITE_API_URL) missingVars.push("VITE_API_URL");
  if (!VITE_NUVIDIO) missingVars.push("VITE_NUVIDIO");
  const errorMessage = `[ENV] Variáveis do env-config.js não carregadas corretamente: ${missingVars.join(", ")}.`;
  console.error(errorMessage, ENV);
  throw new Error(errorMessage);
}

/* ---------------- Styled Components ---------------- */
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column; /* Para empilhar a logo e o card */
  align-items: center;
  justify-content: center;
  height: 100vh; /* Ocupa a altura total da viewport, sem header */
  width: 100%;
  background-color: var(--gray-50); /* Usando Design Token */
  padding: 1rem;
  overflow: hidden;
`;

const Box = styled.div`
  background-color: white;
  border-radius: var(--radius); /* Usando Design Token */
  padding: 2.5rem 2rem; /* padding: 40px 32px; */
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem; /* gap: 16px; */
`;

const LogoImage = styled.img`
  height: 2rem; /* h-8 */
  margin-bottom: 1rem; /* Espaçamento da logo para o título */
`;

const Title = styled.h1`
  font-size: var(--fs-h2); /* Usando Design Token */
  font-weight: 700;
  color: var(--text-900); /* Usando Design Token */
  margin-bottom: 0.5rem; /* margin-bottom: 8px; */
`;

const Subtitle = styled.p`
  font-size: var(--fs-body); /* Usando Design Token */
  color: var(--gray-700); /* Usando Design Token */
  text-align: center;
`;

const Button = styled.button`
  background-color: var(--brand-500); /* Usando Design Token */
  color: white;
  border: none;
  border-radius: var(--radius); /* Usando Design Token */
  padding: 0.75rem 1.5rem; /* padding: 12px 24px; */
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--brand-600); /* Usando Design Token */
  }

  &:disabled {
    background-color: var(--gray-300); /* Usando Design Token */
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div<{ type: "success" | "error" }>`
  background-color: ${(props) =>
    props.type === "success" ? "#e6f7e6" : "#ffeaea"}; /* Cores específicas, pode criar tokens se for recorrente */
  color: ${(props) => (props.type === "success" ? "#1e7b1e" : "#c00")}; /* Cores específicas */
  border: 1px solid
    ${(props) => (props.type === "success" ? "#b6e3b6" : "#ffcccc")};
  border-radius: var(--radius); /* Usando Design Token */
  padding: 0.75rem 1rem; /* padding: 12px 16px; */
  width: 100%;
  text-align: center;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
`;

const Note = styled.p`
  font-size: 0.75rem;
  color: var(--gray-500); /* Usando Design Token */
  text-align: center;
  margin-top: 0.5rem; /* margin-top: 8px; */
`;

/* ==========================================================================
 * Component
 * ========================================================================== */
export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Usar useRef para garantir que a lógica de validação do DNA e JWT só seja executada uma vez por montagem
  const dnaValidationAttempted = useRef(false);
  const jwtValidationAttempted = useRef(false);

  const sessionIdRef = useRef(Math.floor(1000 + Math.random() * 9000).toString());

  const [dna, setDna] = useState<string | null>(null);
  const [isDNAValid, setIsDNAValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // Renomeado 'error' para 'errorMessage' para evitar conflito
  const [validatingDNA, setValidatingDNA] = useState(true);
  const [authenticatingOperator, setAuthenticatingOperator] = useState(false);

  const isLoading = validatingDNA || authenticatingOperator;

  /* ==========================================================================
   * DNA da sessão (primeiro useEffect)
   * ========================================================================== */
  useEffect(() => {
    // Esta parte é mais de inicialização do estado, deve rodar uma vez.
    const dnaSession = sessionStorage.getItem("dna");
    if (dnaSession) {
      setDna(dnaSession);
    } else {
      // Se não tem DNA, paramos aqui e mostramos erro.
      setErrorMessage("DNA não capturado. Volte e realize a captura.");
      showManagedToast("error", "DNA da máquina não encontrado. Acesse a URL inicial novamente.", "dna-missing");
      setValidatingDNA(false);
    }
  }, []); // Array de dependências vazio para rodar apenas na montagem

  /* ==========================================================================
   * Validação do DNA (segundo useEffect)
   * ========================================================================== */
  const validateDNAEffect = useCallback(async () => {
    if (!dna || dnaValidationAttempted.current) {
      return;
    }

    dnaValidationAttempted.current = true; // Marca que a validação do DNA começou

    setValidatingDNA(true);
    setErrorMessage(null); // Limpa erros anteriores
    dismissManagedToast("dna-validation-error"); // Limpa qualquer toast de erro de validação de DNA anterior

    try {
      const token = await fetchAuthToken();
      const isValid = await validateDNA(dna, token);

      if (isValid) {
        setIsDNAValid(true);
        showManagedToast("success", "DNA validado com sucesso.", "dna-validation-success");
      } else {
        const msg = "Máquina não autorizada ou DNA inválido.";
        setErrorMessage(msg);
        showManagedToast("error", msg, "dna-validation-error");
      }
    } catch (err) {
      console.error("Erro ao validar DNA:", err);
      const msg = "Erro ao validar DNA. Tente novamente mais tarde.";
      setErrorMessage(msg);
      showManagedToast("error", msg, "dna-validation-error");
    } finally {
      setValidatingDNA(false);
    }
  }, [dna]); // Depende do 'dna'

  useEffect(() => {
    validateDNAEffect();
  }, [validateDNAEffect]);


  /* ==========================================================================
   * Processamento do retorno do VIDAaS (terceiro useEffect)
   * ========================================================================== */
  const processVidaasCallback = useCallback(async () => {
    const id_token = searchParams.get("id_token");
    if (!id_token || jwtValidationAttempted.current) {
      return;
    }
    jwtValidationAttempted.current = true; // Marca que o processamento do JWT começou

    setAuthenticatingOperator(true);
    setErrorMessage(null); // Limpa erros anteriores
    dismissManagedToast("jwt-error"); // Limpa qualquer toast de erro JWT/operador anterior

    try {
      const jwt = id_token.split("&")[0];
      if (!jwt.includes(".") || jwt.split(".").length !== 3) {
        throw new Error("Token de autenticação inválido.");
      }

      const decoded = decodeJWT(jwt);
      const cpf = decoded?.payload?.sub;

      if (!cpf) {
        throw new Error("CPF não encontrado no token de autenticação.");
      }

      const token = await fetchAuthToken();
      const operator = await fetchOperatorData(cpf, token);

      if (operator.status !== "ATIVO") {
        throw new Error("Operador não autorizado ou inativo.");
      }

      sessionStorage.setItem("operator", JSON.stringify(operator));
      showManagedToast("success", "Operador autenticado com sucesso.", "operator-auth-success");

      // Limpa a URL para deixar somente "/login" e navega
      window.history.replaceState({}, document.title, "/login");
      navigate("/agrRoom", { replace: true });

    } catch (err: any) {
      console.error("Erro no processamento do VIDAaS ou validação do operador:", err);
      const msg = err.message || "Erro ao validar operador. Tente novamente.";
      setErrorMessage(msg);
      showManagedToast("error", msg, "jwt-error");
      navigate("/login", { replace: true }); // Redireciona de volta para /login em caso de erro no JWT
    } finally {
      setAuthenticatingOperator(false);
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    processVidaasCallback();
  }, [processVidaasCallback]);


  /* ==========================================================================
   * Handlers
   * ========================================================================== */
  const handleLoginVidaas = () => {
    // Antes de redirecionar, podemos limpar os toasts ativos para evitar que eles sigam para a próxima tela.
    dismissManagedToast("dna-validation-error");
    dismissManagedToast("dna-validation-success");
    dismissManagedToast("dna-missing");
    dismissManagedToast("jwt-error");
    dismissManagedToast("operator-auth-success");

    const redirect = encodeURIComponent(window.location.href);
    const authUrl = `${VITE_VIDAS_URL}?redirect_uri=${redirect}&state=${sessionIdRef.current}`;
    window.location.href = authUrl;
  };

  const handleLogout = () => {
    sessionStorage.removeItem("dna");
    sessionStorage.removeItem("operator");
    // Mensagem de logout não precisa ser gerenciada como erro/sucesso de processo
    showManagedToast("info", "Sessão encerrada.", "logout-info");
    navigate("/", { replace: true });
  };

  /* ==========================================================================
   * Render
   * ========================================================================== */
  return (
      <PageWrapper>
        <Box>
          <LogoImage src="/logo-vidas.png" alt="Vidas" /> {/* Logo centralizada */}
          <Subtitle>Para acessar, autentique-se usando o aplicativo VIDaaS.</Subtitle>

          {isLoading ? (
              <StatusMessage type="success">
                {validatingDNA && "Validando DNA..."}
                {authenticatingOperator && "Validando Operador..."}
              </StatusMessage>
          ) : errorMessage ? ( /* Usa errorMessage aqui */
              <StatusMessage type="error">{errorMessage}</StatusMessage>
          ) : (
              isDNAValid && <StatusMessage type="success">DNA validado com sucesso.</StatusMessage>
          )}

          <Button onClick={handleLoginVidaas} disabled={!isDNAValid || !!errorMessage || isLoading}>
            {isLoading ? <BeatLoader color="#fff" size={10} /> : "Login com VIDAaS"}
          </Button>

          <Button onClick={handleLogout} disabled={isLoading}>
            Voltar
          </Button>

          <Note>*A leitura do DNA inicia automaticamente na tela anterior.</Note>
        </Box>
      </PageWrapper>
  );
}